import { create } from 'zustand'
import { annBBox, translateAnn } from '@/lib/geometry'
import { uid } from '@/lib/id'
import { blankPage, closeAllSources, loadSource } from '@/lib/pdf/sources'
import type { Annotation, DocSnapshot, PageRef, Rotation, SourceInfo } from '@/types'

const HISTORY_LIMIT = 200

export type DocStatus = 'empty' | 'loading' | 'ready' | 'error'

interface DocState extends DocSnapshot {
  status: DocStatus
  error: string | null
  /** id used for autosave / recent files */
  sessionId: string | null
  fileName: string
  fileHandle: FileSystemFileHandle | null
  sources: SourceInfo[]
  past: DocSnapshot[]
  future: DocSnapshot[]
  /** changed since last save to a file */
  dirty: boolean
  /** bumps whenever anything changes; drives autosave */
  revision: number
  /** bumps when a form field changes in pdf.js storage */
  formRevision: number
}

interface DocActions {
  open(args: {
    bytes: Uint8Array
    name: string
    handle?: FileSystemFileHandle | null
    password?: string
    sessionId?: string
    restore?: DocSnapshot & {
      sources?: SourceInfo[]
      extraSources?: { id: string; name: string; bytes: Uint8Array }[]
    }
  }): Promise<void>
  close(): Promise<void>
  setStatus(status: DocStatus, error?: string | null): void
  rename(name: string): void
  setFileHandle(h: FileSystemFileHandle | null): void
  markSaved(): void
  markFormChanged(): void

  /** Apply a change and record it in undo history. */
  commit(recipe: (s: DocSnapshot) => DocSnapshot): void
  /** Apply a change without recording history (for live drags). Call checkpoint() first. */
  live(recipe: (s: DocSnapshot) => DocSnapshot): void
  /** Push the given snapshot to the undo stack (start of a drag). */
  checkpoint(before: DocSnapshot): void
  /** Revert to the last undo snapshot and drop it (for abandoned creations). */
  discardLast(): void
  snapshot(): DocSnapshot
  undo(): void
  redo(): void

  addAnnotation(a: Annotation): void
  updateAnnotation(id: string, patch: Partial<Annotation>, opts?: { history?: boolean }): void
  deleteAnnotations(ids: string[]): void
  duplicateAnnotations(ids: string[]): string[]
  bringToFront(ids: string[]): void

  rotatePages(ids: string[], delta: 90 | -90 | 180): void
  deletePages(ids: string[]): void
  duplicatePages(ids: string[]): void
  insertBlankPage(index: number, size?: { width: number; height: number }): void
  reorderPages(order: string[]): void
  insertFile(bytes: Uint8Array, name: string, index: number): Promise<number>
}

const initial: DocState = {
  status: 'empty',
  error: null,
  sessionId: null,
  fileName: '',
  fileHandle: null,
  sources: [],
  pages: [],
  annotations: {},
  past: [],
  future: [],
  dirty: false,
  revision: 0,
  formRevision: 0,
}

export function findAnnotation(s: DocSnapshot, id: string): Annotation | undefined {
  for (const list of Object.values(s.annotations)) {
    const a = list.find((x) => x.id === id)
    if (a) return a
  }
  return undefined
}

export const useDoc = create<DocState & DocActions>()((set, get) => ({
  ...initial,

  async open({ bytes, name, handle = null, password, sessionId, restore }) {
    await closeAllSources()
    set({ ...initial, status: 'loading', fileName: name })
    const primaryId = restore?.sources?.[0]?.id
    const { source, pages } = await loadSource(bytes, name, { id: primaryId, password })
    const sources: SourceInfo[] = [source.info]
    for (const extra of restore?.extraSources ?? []) {
      const loaded = await loadSource(extra.bytes, extra.name, { id: extra.id })
      sources.push(loaded.source.info)
    }
    set({
      status: 'ready',
      sessionId: sessionId ?? uid('doc'),
      fileName: name,
      fileHandle: handle,
      sources,
      pages: restore?.pages?.length ? restore.pages : pages,
      annotations: restore?.annotations ?? {},
      dirty: Boolean(restore && (restore.pages?.length || Object.keys(restore.annotations ?? {}).length)),
      revision: 0,
    })
  },

  async close() {
    await closeAllSources()
    set({ ...initial })
  },

  setStatus: (status, error = null) => set({ status, error }),
  rename: (fileName) => set((s) => ({ fileName, revision: s.revision + 1 })),
  setFileHandle: (fileHandle) => set({ fileHandle }),
  markSaved: () => set({ dirty: false }),
  markFormChanged: () =>
    set((s) => ({ dirty: true, formRevision: s.formRevision + 1, revision: s.revision + 1 })),

  snapshot: () => {
    const { pages, annotations } = get()
    return { pages, annotations }
  },

  commit(recipe) {
    const s = get()
    const before = { pages: s.pages, annotations: s.annotations }
    const next = recipe(before)
    if (next.pages === before.pages && next.annotations === before.annotations) return
    set({
      ...next,
      past: [...s.past, before].slice(-HISTORY_LIMIT),
      future: [],
      dirty: true,
      revision: s.revision + 1,
    })
  },

  live(recipe) {
    const s = get()
    const next = recipe({ pages: s.pages, annotations: s.annotations })
    set({ ...next, dirty: true, revision: s.revision + 1 })
  },

  checkpoint(before) {
    set((s) => ({ past: [...s.past, before].slice(-HISTORY_LIMIT), future: [] }))
  },

  discardLast() {
    const s = get()
    const prev = s.past.at(-1)
    if (!prev) return
    set({ ...prev, past: s.past.slice(0, -1), revision: s.revision + 1 })
  },

  undo() {
    const s = get()
    const prev = s.past.at(-1)
    if (!prev) return
    set({
      ...prev,
      past: s.past.slice(0, -1),
      future: [{ pages: s.pages, annotations: s.annotations }, ...s.future],
      dirty: true,
      revision: s.revision + 1,
    })
  },

  redo() {
    const s = get()
    const next = s.future[0]
    if (!next) return
    set({
      ...next,
      past: [...s.past, { pages: s.pages, annotations: s.annotations }],
      future: s.future.slice(1),
      dirty: true,
      revision: s.revision + 1,
    })
  },

  addAnnotation(a) {
    get().commit((s) => ({
      ...s,
      annotations: { ...s.annotations, [a.pageId]: [...(s.annotations[a.pageId] ?? []), a] },
    }))
  },

  updateAnnotation(id, patch, opts = {}) {
    const recipe = (s: DocSnapshot): DocSnapshot => {
      for (const [pid, list] of Object.entries(s.annotations)) {
        const i = list.findIndex((x) => x.id === id)
        if (i >= 0) {
          const copy = list.slice()
          copy[i] = { ...list[i], ...patch, updatedAt: Date.now() } as Annotation
          return { ...s, annotations: { ...s.annotations, [pid]: copy } }
        }
      }
      return s
    }
    if (opts.history === false) get().live(recipe)
    else get().commit(recipe)
  },

  deleteAnnotations(ids) {
    if (!ids.length) return
    const set_ = new Set(ids)
    get().commit((s) => {
      const annotations: DocSnapshot['annotations'] = {}
      for (const [pid, list] of Object.entries(s.annotations))
        annotations[pid] = list.filter((a) => !set_.has(a.id))
      return { ...s, annotations }
    })
  },

  duplicateAnnotations(ids) {
    const created: string[] = []
    get().commit((s) => {
      const annotations = { ...s.annotations }
      for (const id of ids) {
        const a = findAnnotation(s, id)
        if (!a) continue
        const page = s.pages.find((p) => p.id === a.pageId)
        const b = annBBox(a)
        const off = page && b.x + b.w + 12 > page.width ? -12 : 12
        const copy = { ...translateAnn(a, off, 12), id: uid('ann'), createdAt: Date.now() }
        created.push(copy.id)
        annotations[a.pageId] = [...(annotations[a.pageId] ?? []), copy]
      }
      return { ...s, annotations }
    })
    return created
  },

  bringToFront(ids) {
    const set_ = new Set(ids)
    get().commit((s) => {
      const annotations: DocSnapshot['annotations'] = {}
      for (const [pid, list] of Object.entries(s.annotations))
        annotations[pid] = [...list.filter((a) => !set_.has(a.id)), ...list.filter((a) => set_.has(a.id))]
      return { ...s, annotations }
    })
  },

  rotatePages(ids, delta) {
    const set_ = new Set(ids)
    get().commit((s) => ({
      ...s,
      pages: s.pages.map((p) =>
        set_.has(p.id) ? { ...p, rotation: ((((p.rotation + delta) % 360) + 360) % 360) as Rotation } : p,
      ),
    }))
  },

  deletePages(ids) {
    const set_ = new Set(ids)
    get().commit((s) => {
      const pages = s.pages.filter((p) => !set_.has(p.id))
      if (!pages.length) return s // never delete the last page
      const annotations = { ...s.annotations }
      for (const id of ids) delete annotations[id]
      return { pages, annotations }
    })
  },

  duplicatePages(ids) {
    const set_ = new Set(ids)
    get().commit((s) => {
      const pages: PageRef[] = []
      const annotations = { ...s.annotations }
      for (const p of s.pages) {
        pages.push(p)
        if (set_.has(p.id)) {
          const copy = { ...p, id: uid('pg') }
          pages.push(copy)
          annotations[copy.id] = (s.annotations[p.id] ?? []).map((a) => ({
            ...a,
            id: uid('ann'),
            pageId: copy.id,
          }))
        }
      }
      return { pages, annotations }
    })
  },

  insertBlankPage(index, size) {
    get().commit((s) => {
      const ref = s.pages[Math.max(0, Math.min(index, s.pages.length) - 1)] ?? s.pages[0]
      const w = size?.width ?? ref?.width ?? 612
      const h = size?.height ?? ref?.height ?? 792
      const pages = s.pages.slice()
      pages.splice(index, 0, blankPage(w, h))
      return { ...s, pages }
    })
  },

  reorderPages(order) {
    get().commit((s) => {
      const byId = new Map(s.pages.map((p) => [p.id, p]))
      const pages = order.map((id) => byId.get(id)).filter((p): p is PageRef => Boolean(p))
      if (pages.length !== s.pages.length) return s
      return { ...s, pages }
    })
  },

  async insertFile(bytes, name, index) {
    const { source, pages } = await loadSource(bytes, name)
    set((s) => ({ sources: [...s.sources, source.info] }))
    get().commit((s) => {
      const next = s.pages.slice()
      next.splice(index, 0, ...pages)
      return { ...s, pages: next }
    })
    return pages.length
  },
}))
